import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  addPolicyHBException,
  deletePolicyHBException,
  editPolicyHBException,
  getPolicyHBExceptionById,
  getPolicyHBExceptions,
} from '../../../../api/endpoints/corporate';
import type { CorporatePolicyHBException } from '../../../../api/types/corporate';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { useFreshEditRow } from '../../../../components/useFreshEditRow';

const schema = z.object({
  maxHour: z.coerce.number().min(0),
  maxBudget: z.coerce.number().min(0),
  businessClass: z.boolean(),
  firstClass: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = { maxHour: 0, maxBudget: 0, businessClass: false, firstClass: false };

function toFormValues(row: CorporatePolicyHBException): FormValues {
  return {
    maxHour: row.maxHour ?? 0,
    maxBudget: row.maxBudget ?? 0,
    businessClass: row.businessClass ?? false,
    firstClass: row.firstClass ?? false,
  };
}

/** Saat bazlı (uçuşa kalan süre) istisnaları — Policy/*PolicyHBException routes.
 *  NOT hotel-related despite the "HB" prefix — see api/types/corporate.ts file header. */
export function HBExceptionsPanel({ policyID }: { policyID: number }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CorporatePolicyHBException | null>(null);
  const [deleting, setDeleting] = useState<CorporatePolicyHBException | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const queryKey = ['corporate', 'policyHBExceptions', policyID];

  const query = useQuery({ queryKey, queryFn: () => getPolicyHBExceptions(policyID) });
  const rows: CorporatePolicyHBException[] = query.data?.corporateFlightExceptionsHB ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deletePolicyHBException(id),
    onSuccess: () => {
      enqueueSnackbar('İstisna silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">Saat Bazlı İstisnalar (Uçuşa Kalan Süre)</Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni İstisna
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={20} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Maks. Saat</TableCell>
              <TableCell>Maks. Bütçe</TableCell>
              <TableCell>Business</TableCell>
              <TableCell>First</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı istisna bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.flightExceptionID} hover>
                <TableCell>{row.maxHour}</TableCell>
                <TableCell>{row.maxBudget}</TableCell>
                <TableCell>{row.businessClass ? 'Evet' : 'Hayır'}</TableCell>
                <TableCell>{row.firstClass ? 'Evet' : 'Hayır'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <HBExceptionFormDialog
        open={createOpen}
        policyID={policyID}
        row={null}
        onClose={() => setCreateOpen(false)}
        queryKey={queryKey}
      />
      <HBExceptionFormDialog
        open={!!editing}
        policyID={policyID}
        row={editing}
        onClose={() => setEditing(null)}
        queryKey={queryKey}
      />

      <ConfirmDialog
        open={!!deleting}
        title="İstisnayı sil"
        description="Bu saat bazlı istisnayı silmek istediğinize emin misiniz?"
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.flightExceptionID)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}

function HBExceptionFormDialog({
  open,
  policyID,
  row,
  onClose,
  queryKey,
}: {
  open: boolean;
  policyID: number;
  row: CorporatePolicyHBException | null;
  onClose: () => void;
  queryKey: (string | number)[];
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!row;

  const { control, handleSubmit, register, reset } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues,
  });

  // Refetch the fresh row on edit-open; silently falls back to the list row on error.
  const seed = useFreshEditRow({
    open,
    editing: row,
    queryKey: ['corporate', 'policyHBException', row?.flightExceptionID],
    fetch: async (r) => (await getPolicyHBExceptionById(r.flightExceptionID)).corporateFlightExceptionsHB?.[0],
  });

  useEffect(() => {
    if (open) reset(seed ? toFormValues(seed) : emptyValues);
  }, [open, seed, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && row
        ? editPolicyHBException({ ...values, policyID, flightExceptionID: row.flightExceptionID })
        : addPolicyHBException({ ...values, policyID }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'İstisna güncellendi.' : 'İstisna oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'İstisnayı Güncelle' : 'Yeni Saat Bazlı İstisna'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Uçuşa Kalan Maks. Saat"
                {...register('maxHour')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" type="number" label="Maks. Bütçe" {...register('maxBudget')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="businessClass"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Business Class"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="firstClass"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="First Class"
                  />
                )}
              />
            </Grid>
          </Grid>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
